import { ref, computed, onMounted, onUnmounted } from "vue";

interface TimeValues {
	days: number;
	hours: number;
	minutes: number;
	seconds: number;
}

export const useCountdown = (targetDateInput: string | Date | null) => {
	const timeLeft = ref(0);
	const isExpired = ref(false);
	let timer: NodeJS.Timeout | null = null;
	let target: Date;

	if (!targetDateInput) {
		return null;
	}

	if (
		typeof targetDateInput === "string" &&
		targetDateInput.startsWith("/Date(")
	) {
		const matches = targetDateInput.match(/\/Date\((\d+)([+-]\d{4})\)\//);
		const matchValue = matches?.[1];

		if (!matchValue) {
			return null;
		}

		const timestamp = parseInt(matchValue, 10);
		target = new Date(timestamp);
	} else if (typeof targetDateInput === "string") {
		target = new Date(targetDateInput);
	} else {
		target = targetDateInput;
	}

	if (!target || isNaN(target.getTime())) {
		return null;
	}

	const timeValues = computed<TimeValues>(() => {
		if (isExpired.value) {
			return {
				days: 0,
				hours: 0,
				minutes: 0,
				seconds: 0
			};
		}

		return {
			days: Math.floor(timeLeft.value / (1000 * 60 * 60 * 24)),
			hours: Math.floor(
				(timeLeft.value % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
			),
			minutes: Math.floor((timeLeft.value % (1000 * 60 * 60)) / (1000 * 60)),
			seconds: Math.floor((timeLeft.value % (1000 * 60)) / 1000)
		};
	});

	const updateTimer = () => {
		const now = new Date().getTime();
		const distance = target.getTime() - now;

		if (distance <= 0) {
			timeLeft.value = 0;
			isExpired.value = true;
			if (timer) clearInterval(timer);
			return;
		}

		timeLeft.value = distance;
	};

	onMounted(() => {
		updateTimer();
		timer = setInterval(updateTimer, 1000);
	});

	onUnmounted(() => {
		if (timer) clearInterval(timer);
	});

	return {
		timeValues,
		isExpired
	};
};
